"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Clock, MapPin, ArrowRight, Calendar, Tag } from "lucide-react";
import { useSession } from "next-auth/react";
import { IPackageRecord } from "../interface/IPackageData.interface";
import { months } from "@/data/tourPackagesData";
import { useModal } from "@/core/context/ModalContext";
import { showErrorMessage } from "@/utils/toastMessage/toast.message";
import RichText from "@/utils/richText";

interface IProps {
  activeTab: string;
  content: IPackageRecord[];
}

const ListView: React.FC<IProps> = ({ content }) => {
  const { status } = useSession();
  const { openModal } = useModal();
  const isLoggedIn = status === "authenticated";

  const handleNotLoggedIn = () => {
    openModal("login");
    showErrorMessage("Please login to access this feature.");
  };

  return (
    <div className="flex flex-col gap-8 py-4">
      {content.map((packageItem) => (
        <div
          key={packageItem.id}
          className="group relative flex flex-col md:flex-row bg-white rounded-2xl overflow-hidden cursor-pointer border border-primary-100 transition-all duration-300 ease-in-out"
        >
          <div className="relative w-full md:w-80 lg:w-[400px] h-64 md:h-auto overflow-hidden">
            <Link href={`/package/${packageItem?.slug}`}>
              <Image
                src={packageItem?.image || "/assest/images/placeholder.png"}
                alt={packageItem?.name || "Tour Package"}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </Link>

            <div className="absolute top-4 left-4">
              <span className="flex items-center gap-1.5 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-bold text-primary-500 shadow-sm uppercase tracking-wider">
                <MapPin size={14} className="text-primary-500" />
                {packageItem?.destination}
              </span>
            </div>
          </div>

          <div className="flex flex-col flex-1 p-6 lg:p-8">
            <div className="flex flex-col h-full">
              <div className="flex justify-between items-center gap-4 mb-3">
                <Link
                  href={`/package/${packageItem?.slug}`}
                  className="flex-1 hover:underline"
                >
                  <h2 className="text-xl lg:text-2xl font-bold line-clamp-1">
                    {packageItem?.name}
                  </h2>
                </Link>
                <div className="hidden lg:flex items-center gap-4">
                  <div className="flex items-center gap-1 text-primary-500 font-medium">
                    <Tag size={16} className="text-primary-500" />
                    {packageItem?.package_type?.name}
                  </div>
                  <div className="flex items-center gap-1 text-primary-500 font-medium">
                    <Clock size={16} className="text-primary-500" />
                    {packageItem?.duration} Days
                  </div>
                </div>
              </div>

              <RichText
                content={packageItem?.description || ""}
                className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-6"
              />

              <div className="mt-auto pt-6 border-t border-primary-50">
                <div className="flex flex-wrap items-center justify-between gap-6">
                  <div className="flex flex-col">
                    <span className="text-xs text-primary-400 font-medium uppercase tracking-tight">
                      Price per person
                    </span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-black text-primary-900">
                        NPR {packageItem?.current_price}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {isLoggedIn ? (
                      <Link href={`/user/book/${packageItem?.id}`}>
                        <button className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-primary-500 hover:bg-primary-600 shadow-lg shadow-primary-200 transition-all active:scale-95">
                          Book Now <ArrowRight size={16} />
                        </button>
                      </Link>
                    ) : (
                      <button
                        onClick={handleNotLoggedIn}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-primary-500 hover:bg-primary-600 transition-all active:scale-95"
                      >
                        Book Now <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-3 bg-primary-50/80 p-3 rounded-2xl">
                  <Calendar size={14} className="text-primary-400" />
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {months.map((month) => {
                      const isAvailable = packageItem?.availability_month?.some(
                        (m: string) => {
                          const formatted = m.toLowerCase().trim();
                          return (
                            formatted === month.toLowerCase() ||
                            formatted.startsWith(
                              month.slice(0, 3).toLowerCase()
                            )
                          );
                        }
                      );

                      return (
                        <span
                          key={month}
                          className={`text-[10px] uppercase font-bold tracking-widest ${
                            isAvailable
                              ? "text-primary-500"
                              : "text-primary-300 line-through decoration-primary-300"
                          }`}
                        >
                          {month.slice(0, 3)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ListView;
